import type { Translator } from '@praesto/shared'
import { useText } from '../i18n.js'
import { useKeepInView } from './keepInView.js'
import { usePopover } from './usePopover.js'
import type { RemotePresence } from '../collab/usePresence.js'

export interface Me {
  name: string
  color: string
  isGuest: boolean
  isPresenting: boolean
}

/** Wie viele Avatare in der Leiste stehen, bevor zusammengefasst wird. */
const AVATAR_LIMIT = 5

function statusOf(
  remote: RemotePresence,
  jemandPraesentiert: boolean,
  t: Translator,
): string {
  if (remote.presenting) return t('participants.presenting')
  if (!jemandPraesentiert) {
    return remote.user.isGuest ? t('participants.watching') : t('participants.editing')
  }
  return remote.following ? t('participants.following') : t('participants.lookingAround')
}

/**
 * Teilnehmerliste.
 *
 * Der Vortragende soll auf einen Blick sehen, wer im Raum ist — mit Namen, nicht
 * als anonyme Punkte. Der Folgestatus kommt aus der Awareness und ist deshalb
 * ehrlich: „folgt" heißt, dass diese Person wirklich dasselbe sieht.
 */
export function Participants({
  presence,
  me,
}: {
  presence: RemotePresence[]
  me: Me
}): React.ReactElement {
  const t = useText()
  const { open, toggle, ref: rootRef } = usePopover<HTMLDivElement>()
  const listRef = useKeepInView<HTMLDivElement>()

  const jemandPraesentiert = me.isPresenting || presence.some((r) => r.presenting)
  const sichtbar = presence.slice(0, AVATAR_LIMIT)
  const gesamt = presence.length + 1

  return (
    <div className="participants" ref={rootRef}>
      <button
        className="avatars"
        onClick={toggle}
        aria-expanded={open}
        title={t.plural(gesamt, 'participants.one', 'participants.many')}
      >
        <span className="avatar is-me" style={{ backgroundColor: me.color }}>
          {me.name.slice(0, 1).toUpperCase()}
        </span>
        {sichtbar.map((remote) => (
          <span
            key={remote.clientId}
            className="avatar"
            style={{ backgroundColor: remote.user.color }}
          >
            {remote.user.name.slice(0, 1).toUpperCase()}
          </span>
        ))}
        {presence.length > AVATAR_LIMIT && (
          <span className="avatar is-more">+{presence.length - AVATAR_LIMIT}</span>
        )}
      </button>

      {open && (
        <div
          className="participants-list"
          role="dialog"
          aria-label={t('participants.title')}
          ref={listRef}
        >
          <header>
            {t('participants.inBoard', {
              people: t.plural(gesamt, 'common.person.one', 'common.person.many'),
            })}
          </header>
          <ul>
            <li>
              <span className="participant-dot" style={{ backgroundColor: me.color }} />
              <span className="participant-name">
                {me.name} <span className="muted">{t('participants.you')}</span>
              </span>
              {me.isGuest && <span className="participant-tag">{t('board.guest')}</span>}
              {me.isPresenting && (
                <span className="participant-status">{t('participants.presenting')}</span>
              )}
            </li>
            {presence.map((remote) => (
              <li key={remote.clientId}>
                <span
                  className="participant-dot"
                  style={{ backgroundColor: remote.user.color }}
                />
                <span className="participant-name">{remote.user.name}</span>
                {remote.user.isGuest && (
                  <span className="participant-tag">{t('board.guest')}</span>
                )}
                <span
                  className={`participant-status${remote.presenting ? ' is-presenting' : ''}`}
                >
                  {statusOf(remote, jemandPraesentiert, t)}
                </span>
              </li>
            ))}
          </ul>
          {presence.length === 0 && (
            <p className="participants-empty">
              Noch niemand dabei. Über „Freigeben" erzeugst du einen Gast-Link.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
